import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaHome,
  FaChevronDown,
  FaChevronUp,
  FaBars,
  FaTimes,
  FaDollarSign,
  FaBox,
  FaBuilding,
  FaUsers,
  FaUsersCog,
  FaClipboardList,
  FaCalendarAlt,
  FaTh,
  FaAddressCard,
  FaCogs
} from 'react-icons/fa';
import axios from '../../config/axios';
import Swal from 'sweetalert2';
import '../../assets/styles/menu.css';

const Menu = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSubmenuItem, setActiveSubmenuItem] = useState(null);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [menuHeight, setMenuHeight] = useState('auto');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1128);
  const [menusUsuario, setMenusUsuario] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);

  const topMenuRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();


  const getIconByMenuName = (menuName) => {
    const iconMap = {
      'Dashboard': FaHome,
      'Agenda': FaCalendarAlt,
      'Operações': FaTh,
      'Financeiro': FaDollarSign,
      'Estoque': FaBox,
      'Pessoas': FaUsers,
      'Serviços': FaUsersCog,
      'Gestoras': FaBuilding,
      'Anamnese': FaClipboardList,
      'Configurações': FaCogs,
      'Meu Visagismo': FaAddressCard
    };

    return iconMap[menuName] || FaTh;
  };

  const toggleMenu = (menu) => {
    if (isMobile) {
      if (openMenu === menu) {
        setOpenMenu(null);
      } else {
        setOpenMenu(menu);
      }
    } else {
      if (openMenu === menu) {
        setOpenMenu(null);
        setActiveSubmenuItem(null);
      } else {
        setOpenMenu(menu);
      }
    }
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      setOpenMenu(null);
    }
  };

  const handleSubmenuClick = (item, parentMenu) => {
    setActiveSubmenuItem(item);

    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  const handleMenuItemClick = (item) => {
    setActiveMenuItem(item);
    setOpenMenu(null);
    setActiveSubmenuItem(null);

    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 1128;
      setIsMobile(newIsMobile);

      if (newIsMobile) {
        if (isMenuOpen) {
          setMenuHeight('auto');
        } else {
          setMenuHeight('70px');
        }
      } else {
        const hasOpenSubmenu = openMenu && menusUsuario.some(menu =>
          menu.nome.toLowerCase() === openMenu && menu.submenus && menu.submenus.length > 0
        );
        setMenuHeight(hasOpenSubmenu ? '120px' : '70px');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [openMenu, isMenuOpen, isMobile, menusUsuario]);

  const fetchMenusUsuario = async () => {
    try {
      const menusArmazenados = localStorage.getItem('menusUsuario');
      
      if (menusArmazenados) {
        setMenusUsuario(JSON.parse(menusArmazenados));
        setLoadingMenus(false);
        return;
      }
      
      Swal.fire({
        title: 'Carregando...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
      
      const response = await axios.get('/menus');
      const menus = response.data;
      
      localStorage.setItem('menusUsuario', JSON.stringify(menus));
      setMenusUsuario(menus);
      Swal.close();
    } catch (error) {
      setMenusUsuario([]);
      Swal.close();
    } finally {
      setLoadingMenus(false);
    }
  };

  useEffect(() => {
    fetchMenusUsuario();
    
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        localStorage.removeItem('menusUsuario');
        localStorage.removeItem('userData');
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    setActiveMenuItem(null);
    setActiveSubmenuItem(null);
    setOpenMenu(null);

    const path = location.pathname;

    if (path === '/dashboard') {
      setActiveMenuItem('dashboard');
    }

    if (menusUsuario.length > 0) {
      menusUsuario.forEach(menu => {
        const menuKey = menu.nome.toLowerCase();

        if (menu.rota === path && (!menu.submenus || menu.submenus.length === 0)) {
          setActiveMenuItem(menuKey);
        }

        if (menu.submenus && menu.submenus.length > 0) {
          menu.submenus.forEach(submenu => {
            if (submenu.rota === path) {
              setOpenMenu(menuKey);
              setActiveSubmenuItem(submenu.nome.toLowerCase());
            }
          });

          if (path.startsWith(menu.rota.split('/')[1] ? `/${menu.rota.split('/')[1]}` : menu.rota)) {
            setOpenMenu(menuKey);
          }
        }
      });
    }
  }, [location.pathname, menusUsuario]);

  const getMenuItemIcon = (menuName) => {
    if (openMenu === menuName) {
      return <FaChevronUp className="menu-expand-icon" />;
    } else {
      return <FaChevronDown className="menu-expand-icon" />;
    }
  };


  const renderDynamicSubmenu = (menu) => {
    if (openMenu !== menu.nome.toLowerCase() || !menu.submenus || menu.submenus.length === 0) return null;

    return (
      <div className="submenu show">
        <ul className="submenu-list">
          {menu.submenus.map((submenu) => (
            <Link key={submenu.id} to={submenu.rota}>
              <li className={`submenu-item ${activeSubmenuItem === submenu.nome.toLowerCase() ? 'active' : ''}`}
                onClick={() => handleSubmenuClick(submenu.nome.toLowerCase(), menu.nome.toLowerCase())}>
                <span>{submenu.nome}</span>
              </li>
            </Link>
          ))}
        </ul>
      </div>
    );
  };

  const renderMenusUsuario = () => {
    if (loadingMenus) {
      return null;
    }

    const menusFiltrados = menusUsuario.filter(menu =>
      menu.nome.toLowerCase() !== 'dashboard'
    );

    return menusFiltrados.map((menu) => {
      const IconComponent = getIconByMenuName(menu.nome);
      const menuKey = menu.nome.toLowerCase();
      const hasSubmenus = menu.submenus && menu.submenus.length > 0;

      if (hasSubmenus) {
        return (
          <div key={menu.id} className="menu-wrapper">
            <li
              className={`menu-item ${openMenu === menuKey ? 'active' : ''}`}
              onClick={() => toggleMenu(menuKey)}
            >
              <div className="menu-item-content">
                <IconComponent />
                <span>{menu.nome}</span>
              </div>
              {getMenuItemIcon(menuKey)}
            </li>
            {renderDynamicSubmenu(menu)}
          </div>
        );
      } else {
        return (
          <Link key={menu.id} to={menu.rota}>
            <li
              className={`menu-item ${activeMenuItem === menuKey ? 'active' : ''}`}
              onClick={() => handleMenuItemClick(menuKey)}
            >
              <div className="menu-item-content">
                <IconComponent />
                <span>{menu.nome}</span>
              </div>
            </li>
          </Link>
        );
      }
    });
  };

  return (
    <div
      className={`menu-container ${isMenuOpen ? 'open' : ''}`}
      style={{ height: menuHeight }}
    >
      <div className="top-menu" ref={topMenuRef}>
        <button className="menu-toggle" onClick={toggleMobileMenu}>
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
        <ul className="menu-list">
          <div className="menu-wrapper">
            <Link to="/dashboard">
              <li
                className={`menu-item ${activeMenuItem === 'dashboard' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('dashboard')}
              >
                <div className="menu-item-content">
                  <FaHome />
                  <span>Dashboard</span>
                </div>
              </li>
            </Link>
          </div>

          {renderMenusUsuario()}
        </ul>
      </div>
    </div>
  );
};

export default Menu;

